/**
 * Hook pour gérer la restauration des sondages en attente
 * Détecte et restaure automatiquement les sondages sauvegardés avant connexion
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { usePolls } from "./usePolls";
import {
  PendingPollService,
  PendingPollData,
} from "../services/PendingPollService";
import { useToast } from "./use-toast";
import { logger } from "../lib/logger";

export interface UsePendingPollRestoreReturn {
  hasPendingPoll: boolean;
  pendingPoll: PendingPollData | null;
  isRestoring: boolean;
  restorePoll: () => Promise<void>;
  dismissPoll: () => void;
}

export function usePendingPollRestore(): UsePendingPollRestoreReturn {
  const { user } = useAuth();
  const { createPoll } = usePolls();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation("common");

  const [pendingPoll, setPendingPoll] = useState<PendingPollData | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  // Détecter la présence d'un sondage en attente au montage
  useEffect(() => {
    // Ne vérifier que si l'utilisateur est connecté
    if (!user) {
      setPendingPoll(null);
      return;
    }

    const poll = PendingPollService.getPendingPoll();
    if (poll) {
      logger.info("📦 [usePendingPollRestore] Pending poll detected", "poll", {
        pollType: poll.pollType,
        title: poll.pollData.title,
        age: PendingPollService.getPendingPollAge(),
      });
      setPendingPoll(poll);
    }
  }, [user]);

  // Restaurer le sondage
  const restorePoll = useCallback(async () => {
    if (!pendingPoll || !user) {
      logger.warn("Cannot restore poll: no pending poll or user", "poll");
      return;
    }

    setIsRestoring(true);

    try {
      logger.info(
        "🔄 [usePendingPollRestore] Starting poll restoration",
        "poll",
        {
          pollType: pendingPoll.pollType,
          title: pendingPoll.pollData.title,
        },
      );

      // Créer le sondage dans Supabase
      const result = await createPoll(pendingPoll.pollData);

      if (!result.poll || result.error) {
        throw new Error(result.error || "Failed to create poll");
      }

      logger.info(
        "✅ [usePendingPollRestore] Poll restored successfully",
        "poll",
        {
          pollId: result.poll.id,
          slug: result.poll.slug,
        },
      );

      // Nettoyer le sessionStorage
      PendingPollService.clearPendingPoll();
      setPendingPoll(null);

      // Toast de succès
      toast({
        title: t("pending_poll_restore.success_title"),
        description: t("pending_poll_restore.success_description"),
        duration: 3000,
      });

      // Rediriger vers l'éditeur selon le type
      const editorPath = getEditorPath(pendingPoll.pollType, result.poll.slug);
      navigate(editorPath);
    } catch (error) {
      logger.error(
        "❌ [usePendingPollRestore] Failed to restore poll",
        "poll",
        error,
      );

      toast({
        title: t("pending_poll_restore.error_title"),
        description: t("pending_poll_restore.error_description"),
        variant: "destructive",
      });
    } finally {
      setIsRestoring(false);
    }
  }, [pendingPoll, user, createPoll, navigate, toast, t]);

  // Ignorer le sondage
  const dismissPoll = useCallback(() => {
    logger.info(
      "🗑️ [usePendingPollRestore] Pending poll dismissed by user",
      "poll",
    );

    PendingPollService.clearPendingPoll();
    setPendingPoll(null);

    toast({
      title: t("pending_poll_restore.dismissed_title"),
      description: t("pending_poll_restore.dismissed_description"),
      duration: 2000,
    });
  }, [toast, t]);

  return {
    hasPendingPoll: pendingPoll !== null,
    pendingPoll,
    isRestoring,
    restorePoll,
    dismissPoll,
  };
}

/**
 * Obtenir le chemin de l'éditeur selon le type de sondage
 */
function getEditorPath(pollType: string, slug: string): string {
  switch (pollType) {
    case "date":
      return `/date/workspace/date?edit=${slug}`;
    case "form":
      return `/form/workspace/form?edit=${slug}`;
    case "quizz":
      return `/quizz/workspace/quizz?edit=${slug}`;
    case "availability":
      return `/availability/workspace/availability?edit=${slug}`;
    default:
      return "/dashboard";
  }
}
